import React, { useEffect, useContext } from 'react';
import { useParams } from 'react-router';
import { Form, Col, Row } from 'react-bootstrap';
import AppContext from 'Utils/AppContext';
import ProdToggle from './ProdToggle';

/**
 * Editor for DynamoDB sources (engine 'dynamodb', datasourceType NOSQL). Unlike the JDBC
 * and Mongo editors there is no host/port/database/protocol — DynamoDB is addressed by
 * AWS region. Credentials are optional: when access key fields are left blank the worker
 * authenticates with its ambient AWS identity (IRSA / instance profile). `endpointUrl`
 * exists for DynamoDB Local and other non-AWS endpoints; `table` optionally scopes
 * discovery to a single table.
 */
const DynamoSourceEditor = ({ connection, setConnection }) => {
  const { rulesList } = useContext(AppContext);
  const { id } = useParams();

  const defaultRules = rulesList.find(rules => rules.default === true);

  const defaults = {
    name: '',
    region: '',
    accessKeyId: '',
    secretAccessKey: '',
    endpointUrl: '',
    table: '',
    type: 'dynamodb',
    ...!id && ({
      scheduleEnabled: true,
      scheduleDayOfWeek: 'SAT',
      scheduleWeekOccurrence: 1,
      scheduleHour: 0,
      scheduleMinute: 0,
      scheduleRules: defaultRules
    })
  }

  // Merge defaults UNDER any fields already populated by the parent SourceForm dropdown
  // (type / customDatabaseType) or a `?prefill=` payload (pipeline board Connect →
  // region/name). A plain `setConnection(defaults)` would wipe those out — same pattern
  // as MongoSourceEditor / DbSourceEditor.
  useEffect(() => { if (!id) setConnection((prev) => ({ ...defaults, ...(prev || {}) })); }, []);

  return (
    <div className='text-left'>
      <Row className='mb-4'>
        <Col lg='12'>
          <Form.Label>Data source name *</Form.Label>
          <Form.Control
            size='sm'
            placeholder='Sample_Dynamodb'
            value={connection.name}
            onChange={(n) => setConnection({ ...connection, name: n.target.value })}
            type='text'
            required
            isInvalid={connection && connection.name && connection.name.includes(' ')}
          />
          <Form.Control.Feedback type="invalid">
            Data source names must not include spaces.
          </Form.Control.Feedback>
        </Col>
      </Row>
      <ProdToggle />
      <Row className='mb-4'>
        <Col className='pr-1'>
          <Form.Label>Region *</Form.Label>
          <Form.Control
            size='sm'
            placeholder='us-east-2'
            value={connection.region ?? ''}
            onChange={(n) => setConnection({ ...connection, region: n.target.value })}
            type='text'
            required
          />
        </Col>
        <Col className='pl-1'>
          <Form.Label>Table</Form.Label>
          <Form.Control
            size='sm'
            placeholder='All tables'
            value={connection.table ?? ''}
            onChange={(n) => setConnection({ ...connection, table: n.target.value })}
            type='text'
          />
          <Form.Text className='text-muted'>
            Optional — scope discovery to one table. Leave blank to discover every table in the region.
          </Form.Text>
        </Col>
      </Row>
      <Row className='mb-4'>
        <Col className='pr-1'>
          <Form.Label>Access key ID</Form.Label>
          <Form.Control
            size='sm'
            type='text'
            value={connection.accessKeyId ?? ''}
            onChange={(n) => setConnection({ ...connection, accessKeyId: n.target.value })}
            placeholder='AKIA…'
            autoComplete='new-password'
          />
        </Col>
        <Col className='pl-1'>
          <Form.Label>Secret access key</Form.Label>
          <Form.Control
            size='sm'
            type='password'
            value={connection.secretAccessKey ?? ''}
            placeholder={id ? 'Leave blank to keep current' : 'Secret access key'}
            onChange={(n) => setConnection({ ...connection, secretAccessKey: n.target.value })}
            autoComplete={id ? 'off' : 'new-password'}
          />
          <Form.Text className='text-muted'>
            Leave both key fields blank to use the platform's AWS identity.
          </Form.Text>
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col>
          <Form.Label>Endpoint URL</Form.Label>
          <Form.Control
            size='sm'
            type='text'
            value={connection.endpointUrl ?? ''}
            onChange={(n) => setConnection({ ...connection, endpointUrl: n.target.value })}
            placeholder='http://localhost:8000'
          />
          <Form.Text className='text-muted'>
            Advanced — override the AWS endpoint, e.g. for DynamoDB Local. Leave blank for AWS.
          </Form.Text>
        </Col>
      </Row>
    </div>
  )
}

export default DynamoSourceEditor;
